/**
 * BtSearchCombobox — Composant unifié de recherche/sélection d'un Bon de Travail.
 *
 * 🎯 Pourquoi ce composant ?
 * Avant : chaque page (Validation des heures, Rapport d'heures, etc.) avait sa propre
 * UI de recherche de BT, avec des comportements et libellés inconsistants.
 * Maintenant : une seule source de vérité, comportement et style identiques partout.
 *
 * Props :
 *   value          (string)              — l'ID du BT actuellement sélectionné
 *   onChange       (fn(btId, btObject))  — callback à la sélection (vide string + null pour clear)
 *   referenceDate  (string?, "YYYY-MM-DD") — si fournie, badge "🎯 CE JOUR" sur les BT
 *                                          dont les dates_chantier matchent
 *   placeholder    (string?)             — texte du champ de recherche
 *   excludeId      (string?)             — un BT à masquer (ex: BT origine d'une segmentation)
 *   autoFocus      (boolean?)            — focus auto à l'ouverture (def: false)
 *   testIdPrefix   (string?)             — préfixe pour les data-testid (def: "bt-combobox")
 *   compact        (boolean?)            — version compacte 1 ligne (def: false, 2 lignes)
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Input } from "./ui/input";
import { Check, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BtSearchCombobox = ({
  value,
  onChange,
  referenceDate,
  placeholder = "🔍 Rechercher par client, projet, dossier, adresse ou n° BT…",
  excludeId,
  autoFocus = false,
  testIdPrefix = "bt-combobox",
  compact = false,
}) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBt, setSelectedBt] = useState(null);
  const debounceRef = useRef(null);

  // Charger le BT actuellement sélectionné (pour afficher la chip) quand value change
  useEffect(() => {
    if (!value) {
      setSelectedBt(null);
      return;
    }
    if (selectedBt && selectedBt.id === value) return;
    // Fetch ce BT précisément
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/crm/bons-travail`);
        const all = res.data?.bons_travail || res.data?.bons || [];
        const found = all.find(b => b.id === value);
        if (!cancelled && found) setSelectedBt(found);
      } catch {/* silencieux */}
    })();
    return () => { cancelled = true; };
  }, [value, selectedBt]);

  // Recherche serveur debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!search || search.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/crm/bons-travail/search?q=${encodeURIComponent(search)}&limit=30`
        );
        let bons = res.data?.bons || [];
        if (excludeId) bons = bons.filter(b => b.id !== excludeId);
        // Tri "CE JOUR" en tête si referenceDate fournie
        if (referenceDate) {
          bons.sort((a, b) => {
            const aMatch = (a.dates_chantier || []).some(d => String(d).slice(0, 10) === referenceDate) ? 1 : 0;
            const bMatch = (b.dates_chantier || []).some(d => String(d).slice(0, 10) === referenceDate) ? 1 : 0;
            return bMatch - aMatch;
          });
        }
        setResults(bons);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, excludeId, referenceDate]);

  const handleSelect = (b) => {
    setSelectedBt(b);
    setSearch("");
    setResults([]);
    onChange?.(b.id, b);
  };

  const handleClear = () => {
    setSelectedBt(null);
    setSearch("");
    setResults([]);
    onChange?.("", null);
  };

  const renderProjet = (b) =>
    b.titre || b.nom_dossier || b.adresse_travaux || b.adresse_complete || b.adresse || "";

  // Affichage de la chip BT sélectionnée
  if (value && selectedBt) {
    const projet = renderProjet(selectedBt);
    return (
      <div
        className="flex items-center justify-between gap-2 border border-blue-300 bg-blue-50 rounded-md px-3 py-2 text-sm"
        data-testid={`${testIdPrefix}-selected`}
      >
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-blue-900 truncate">
            {selectedBt.client_nom || "Client inconnu"}
            {selectedBt.est_interne && (
              <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-normal">INTERNE</span>
            )}
          </div>
          {(projet || selectedBt.numero_bt) && (
            <div className="text-xs text-blue-700 truncate">
              {projet}
              {selectedBt.numero_bt ? `${projet ? " · " : ""}BT #${selectedBt.numero_bt}` : ""}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleClear}
          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-100"
          data-testid={`${testIdPrefix}-clear`}
          title="Changer de BT"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Mode recherche
  return (
    <div className="relative">
      <Input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid={`${testIdPrefix}-search`}
      />
      {(search.trim().length >= 2 || loading) && (
        <div
          className="border rounded-md max-h-60 overflow-y-auto bg-white mt-1 shadow-sm"
          data-testid={`${testIdPrefix}-list`}
        >
          {loading && (
            <div className="p-3 text-xs text-slate-500 italic text-center">Recherche…</div>
          )}
          {!loading && results.length === 0 && (
            <div className="p-3 text-xs text-slate-500 italic text-center">Aucun BT trouvé</div>
          )}
          {!loading && results.map(b => {
            const projet = renderProjet(b);
            const isSelected = b.id === value;
            const dates = (b.dates_chantier || []).filter(Boolean).sort();
            const isExactDay = referenceDate && dates.some(d => String(d).slice(0, 10) === referenceDate);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => handleSelect(b)}
                className={`w-full text-left px-3 py-2 border-b last:border-b-0 text-sm transition-colors ${
                  isSelected ? "bg-blue-50 hover:bg-blue-100" : isExactDay ? "bg-emerald-50 hover:bg-emerald-100" : "hover:bg-slate-50"
                }`}
                data-testid={`${testIdPrefix}-option-${b.id}`}
              >
                <div className="flex items-start gap-2">
                  {isSelected && <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold ${compact ? "text-xs" : "text-sm"} text-slate-900 truncate flex items-center gap-1.5 flex-wrap`}>
                      {isExactDay && (
                        <span className="text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded font-bold">🎯 CE JOUR</span>
                      )}
                      <span>{b.client_nom || "Client inconnu"}</span>
                      {b.est_interne && (
                        <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-normal">INTERNE</span>
                      )}
                      {b.statut && (
                        <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded capitalize font-normal">{b.statut}</span>
                      )}
                    </div>
                    {!compact && (projet || b.numero_bt) && (
                      <div className="text-xs text-slate-500 truncate">
                        {projet}
                        {b.numero_bt ? `${projet ? " · " : ""}BT #${b.numero_bt}` : ""}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {search.trim().length > 0 && search.trim().length < 2 && (
        <p className="text-[10px] text-slate-400 mt-1">Tapez au moins 2 caractères…</p>
      )}
    </div>
  );
};

export default BtSearchCombobox;
